import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, Calendar, Download, Clock, FileText, Search, FileSearch, Timer, Contact, PackageX } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { MOCK_REPORTS } from '../../utils/mockData';
import { Report, ReportType } from '../../types';
import { ReportGenerateSheet } from '../../components/reports/ReportGenerateSheet';
export function Reports() {
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredReports = MOCK_REPORTS.filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const getReportIcon = (type: ReportType) => {
    switch (type) {
      case 'PRESCRIPTION_STATUS':
        return <Stethoscope className="w-6 h-6 text-teal-600" data-id="element-981" />;
      case 'DETAILED_PRESCRIPTION':
        return <FileSearch className="w-6 h-6 text-indigo-600" data-id="element-982" />;
      case 'SLA_REPORT':
        return <Timer className="w-6 h-6 text-blue-600" data-id="element-983" />;
      case 'UNAVAILABLE_PRODUCTS':
        return <PackageX className="w-6 h-6 text-amber-600" data-id="element-984" />;
      case 'CUSTOMER_DETAILS':
        return <Contact className="w-6 h-6 text-violet-600" data-id="element-985" />;
      default:
        return <FileText className="w-6 h-6 text-slate-600" data-id="element-986" />;
    }
  };
  const getIconBg = (type: ReportType) => {
    switch (type) {
      case 'PRESCRIPTION_STATUS':
        return 'bg-teal-50';
      case 'DETAILED_PRESCRIPTION':
        return 'bg-indigo-50';
      case 'SLA_REPORT':
        return 'bg-blue-50';
      case 'UNAVAILABLE_PRODUCTS':
        return 'bg-amber-50';
      case 'CUSTOMER_DETAILS':
        return 'bg-violet-50';
      default:
        return 'bg-slate-50';
    }
  };
  return <div className="space-y-6 pb-12" data-id="element-987">
      <div className="flex items-center justify-between" data-id="element-988">
        <div data-id="element-989">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-990">Reports</h1>
          <p className="text-slate-500" data-id="element-991">
            Generate and schedule business intelligence reports
          </p>
        </div>
        <Button variant="outline" onClick={() => {}} data-id="element-992">
          <Calendar className="w-4 h-4 mr-2" data-id="element-993" />
          Scheduled Reports
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm" data-id="element-994">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-995" />
        <input type="text" placeholder="Search reports..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-slate-400 bg-white" data-id="element-996" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-997">
        {filteredReports.map((report, index) => <motion.div key={report.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} data-id="element-998">
            <Card className="h-full flex flex-col hover:shadow-md transition-shadow" data-id="element-999">
              <CardHeader className="pb-3" data-id="element-1000">
                <div className="flex items-start gap-4" data-id="element-1001">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconBg(report.type as ReportType)}`} data-id="element-1002">
                    {getReportIcon(report.type as ReportType)}
                  </div>
                  <div data-id="element-1003">
                    <CardTitle className="text-base" data-id="element-1004">{report.name}</CardTitle>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2" data-id="element-1005">
                      {report.description}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between space-y-4" data-id="element-1006">
                <div className="space-y-2 pt-2" data-id="element-1007">
                  <div className="flex items-center justify-between text-xs" data-id="element-1008">
                    <span className="text-slate-500 flex items-center gap-1" data-id="element-1009">
                      <Clock className="w-3 h-3" data-id="element-1010" /> Last Generated
                    </span>
                    <span className="font-medium text-slate-700" data-id="element-1011">
                      {report.lastGenerated || 'Never'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs" data-id="element-1012">
                    <span className="text-slate-500 flex items-center gap-1" data-id="element-1013">
                      <FileText className="w-3 h-3" data-id="element-1014" /> Total Runs
                    </span>
                    <span className="font-medium text-slate-700" data-id="element-1015">
                      {report.totalRuns}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-slate-100" data-id="element-1016">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setSelectedReport(report)} data-id="element-1017">
                    <Download className="w-3 h-3 mr-2" data-id="element-1018" />
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>)}
      </div>

      {/* Report Generate Sheet */}
      <ReportGenerateSheet report={selectedReport} isOpen={!!selectedReport} onClose={() => setSelectedReport(null)} data-id="element-1019" />
    </div>;
}